import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const { isAuthenticated, user, userProfile, googleLogin, googleLogout, logout, isAdmin, showOnboardingModal } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const handleLogout = async () => {
    // Logout from Google first
    if (googleLogout) {
      await googleLogout();
    }
    // Then logout from Auth0
    await logout({ returnTo: window.location.origin });
    setIsMenuOpen(false);
  };

  const handleLogin = () => {
    googleLogin();
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenus = () => {
    setIsMenuOpen(false);
  };

  const handleProfileUpdate = () => {
    showOnboardingModal();
    closeMenus();
  };

  const handleAdminNavigation = () => {
    navigate('/admin');
    closeMenus();
  };

  const handleDashboardNavigation = () => {
    navigate('/translator/dashboard');
    closeMenus();
  };

  // Get user initials
  const getUserInitials = () => {
    if (userProfile?.name) {
      return userProfile.name
        .split(' ')
        .map(name => name.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    }
    if (user?.name) {
      return user.name
        .split(' ')
        .map(name => name.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeMenus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className=" mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 text-xl font-bold text-blue-600 hover:text-blue-700 transition-colors">
              <img
                src="/assets/logos/juree-favicon.svg"
                alt="Juree logo"
                className="w-8 h-8 object-contain"
              />
              <span>{t('header.appName')}</span>
            </a>

            {/* Right side: Language Switcher + Hamburger (mobile) / Initials (desktop) */}
            <div className="flex items-center gap-2">
              {/* Language Switcher */}
              <LanguageSwitcher />
              {/* Hamburger Menu Button - Mobile Only */}
              <button
                onClick={toggleMenu}
                className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Open menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>

              {/* Initials Avatar Button / Login Button - Desktop Only */}
              <button
                onClick={isAuthenticated ? toggleMenu : handleLogin}
                className={`hidden md:flex items-center justify-center ${
                  isAuthenticated
                    ? 'w-10 h-10 rounded-full'
                    : 'px-4 py-2 rounded-md'
                } bg-blue-600 text-white hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                aria-label={isAuthenticated ? "Open menu" : "Login"}
              >
                <span className="text-sm font-medium">
                  {isAuthenticated ? getUserInitials() : t('header.login')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile/Desktop Overlay Menu */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
            onClick={closeMenus}
          />

          {/* Menu Content */}
          <div
            ref={menuRef}
            className={`fixed top-0 right-4 h-full w-full max-w-xs bg-white z-50 transform transition-transform duration-300 ease-in-out shadow-xl ${
              isMenuOpen ? 'translate-x-0' : 'translate-x-full'
            } md:max-w-sm md:top-16 md:h-auto md:rounded-l-lg md:shadow-2xl md:right-8`}
          >
            {/* Mobile Header */}
            <div className="md:hidden flex items-center justify-between p-4 border-b border-gray-200">
              <span className="text-lg font-semibold text-gray-900">{t('header.menu')}</span>
              <button
                onClick={closeMenus}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Menu Items */}
            <div className="p-4 space-y-6">
              {/* User Info Section */}
              {isAuthenticated && (
                <div className="pb-6 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full">
                      <span className="text-base font-medium">{getUserInitials()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{userProfile?.name || user?.name || 'User'}</div>
                      <div className="text-sm text-gray-500 truncate">{user?.email}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Dashboard Section */}
              {isAuthenticated && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('header.dashboard')}</h3>
                  <ul className="space-y-2">
                    <li>
                      <button
                        onClick={handleDashboardNavigation}
                        className="flex items-center w-full px-3 py-2 text-gray-700 rounded-md hover:bg-gray-100 transition-colors group"
                      >
                        <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v10a2 2 0 01-2 2H10a2 2 0 01-2-2V5z" />
                        </svg>
                        {t('header.translatorDashboard')}
                      </button>
                    </li>
                  </ul>
                </div>
              )}

              {/* Admin Section */}
              {isAuthenticated && isAdmin && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('header.admin')}</h3>
                  <ul className="space-y-2">
                    <li>
                      <button
                        onClick={handleAdminNavigation}
                        className="flex items-center w-full px-3 py-2 text-gray-700 rounded-md hover:bg-gray-100 transition-colors group"
                      >
                        <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {t('header.updatePrompts')}
                      </button>
                    </li>
                  </ul>
                </div>
              )}

              {/* Account Section */}
              {isAuthenticated && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('header.account')}</h3>
                  <ul className="space-y-2">
                    <li>
                      <button
                        onClick={handleProfileUpdate}
                        className="flex items-center w-full px-3 py-2 text-gray-700 rounded-md hover:bg-gray-100 transition-colors group"
                      >
                        <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t('header.profileUpdate')}
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => {
                          navigate('/settings');
                          closeMenus();
                        }}
                        className="flex items-center w-full px-3 py-2 text-gray-700 rounded-md hover:bg-gray-100 transition-colors group"
                      >
                        <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {t('header.settings')}
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-3 py-2 text-red-600 rounded-md hover:bg-red-50 transition-colors group"
                      >
                        <svg className="w-5 h-5 mr-3 text-red-500 group-hover:text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('header.logout')}
                      </button>
                    </li>
                  </ul>
                </div>
              )}

              {/* Authentication for non-logged in users */}
              {!isAuthenticated && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('header.authentication')}</h3>
                  <ul className="space-y-2">
                    <li>
                      <button
                        onClick={() => {
                          handleLogin();
                          closeMenus();
                        }}
                        className="flex items-center w-full px-3 py-2 text-gray-700 rounded-md hover:bg-gray-100 transition-colors group"
                      >
                        <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                        </svg>
                        {t('header.translatorLogin')}
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
